import { useCallback, useMemo } from "react";

import { useTeamRosters } from "@/pages/TaggingV2/Scorekeeping/hooks/useGameRosters";
import { type GameLineupPlayer, type GameSC } from "@/types/game";

import {
  getLikelySide,
  lineupPlayersToOptions,
  orderPlayerOptions,
  type PlayerField,
  type PlayerOption,
  rosterToOptions,
  toGamePlayerId,
} from "./playerOptions";

/** The game's lineup and substitution players, which list no team. */
const getGamePlayers = (game: GameSC | undefined): GameLineupPlayer[] => [
  ...(game?.lineups?.home_lineup ?? []),
  ...(game?.lineups?.away_lineup ?? []),
  ...(game?.substitutions ?? []).flatMap((sub) => [
    sub.previous_lineup_player ?? {},
    sub.new_lineup_player ?? {},
  ]),
];

/**
 * Game players by option id. A player's lineup entry wins over their
 * substitution entries, since a jersey number edit only updates the lineup.
 */
const toGamePlayersById = (players: GameLineupPlayer[]) => {
  const byId = new Map<string, GameLineupPlayer>();
  for (const player of players) {
    const id = toGamePlayerId(player);
    if (player.player_name && !byId.has(id)) byId.set(id, player);
  }
  return byId;
};

/**
 * Pitcher/hitter options from both teams' omnibus rosters (the ones Tagging V2
 * loads), with the team pitching or batting in that half-inning first.
 */
export const useGamePlayerOptions = (game: GameSC | undefined) => {
  const { home, away } = useTeamRosters(game?.teams);

  const gamePlayers = useMemo(() => getGamePlayers(game), [game]);
  const gamePlayersById = useMemo(
    () => toGamePlayersById(gamePlayers),
    [gamePlayers],
  );

  const { homeFirst, awayFirst } = useMemo(() => {
    const homeOptions = rosterToOptions(home.data?.roster.players ?? []);
    const awayOptions = rosterToOptions(away.data?.roster.players ?? []);
    const gameOptions = lineupPlayersToOptions(gamePlayers);
    return {
      homeFirst: orderPlayerOptions({
        first: homeOptions,
        second: awayOptions,
        gamePlayers: gameOptions,
      }),
      awayFirst: orderPlayerOptions({
        first: awayOptions,
        second: homeOptions,
        gamePlayers: gameOptions,
      }),
    };
  }, [gamePlayers, home.data, away.data]);

  const rosterPlayers = useMemo(
    () =>
      new Map(
        [
          ...(home.data?.roster.players ?? []),
          ...(away.data?.roster.players ?? []),
        ].map((player) => [player.playerId, player]),
      ),
    [home.data, away.data],
  );

  const getPlayersForField = useCallback(
    (field: PlayerField, half?: "top" | "bottom"): PlayerOption[] =>
      getLikelySide(field, half) === "home" ? homeFirst : awayFirst,
    [homeFirst, awayFirst],
  );

  return {
    getPlayersForField,
    /** Every option, for columns that list players from both teams */
    availablePlayers: homeFirst,
    /** The roster player with this id; game-only players have none */
    getRosterPlayer: (playerId: string) => rosterPlayers.get(playerId),
    /** The lineup or substitution entry for a game player's option id */
    getGamePlayer: (playerId: string) => gamePlayersById.get(playerId),
    // A failed roster load doesn't count: the page opens on the game's players
    isLoading: home.isLoading || away.isLoading,
  };
};
